import { Vector3 } from "three";
import { Arcs, ArcsOptions } from "../arcs/Arcs";

/**
 * Configuration for orb arc spawning
 */
export interface OrbArcsConfig {
  /** Size parameter matching morph-particles size (default: 800) */
  size: number;
  /** Maximum arcs at once (default: 5) */
  maxArcs: number;
  /** How often to spawn arcs [min, max] seconds (default: [0.2, 0.5]) */
  spawnInterval: [number, number];
  /** Arc lifetime [min, max] seconds (default: [0.2, 0.5]) */
  arcLifetime: [number, number];
}

const DEFAULT_ORB_CONFIG: OrbArcsConfig = {
  size: 800,
  maxArcs: 5,
  spawnInterval: [0.2, 0.5],
  arcLifetime: [0.6, 1.5],
};

/**
 * Lightning arcs inside the orb (sphere).
 * Arcs radiate from the center to random points on the inner surface,
 * staying within the blue shell area.
 */
export class OrbArcs extends Arcs {
  private orbConfig: OrbArcsConfig;
  private active = false;
  private transitionProgress = 0;

  // Pre-computed orb dimensions
  private radius: number;

  constructor(
    orbConfig: Partial<OrbArcsConfig> = {},
    arcsOptions: ArcsOptions = {},
  ) {
    const config = { ...DEFAULT_ORB_CONFIG, ...orbConfig };

    // Configure the base Arcs class for orb lightning style
    super({
      ...arcsOptions,
      config: {
        animation: {
          horizontalEasing: 0.15,
          verticalEasing: 0.15,
          targetChangeChance: 0.08,
          horizontalRange: 0.5,
          verticalRange: 0.5,
          jaggedness: 30, // Moderate jaggedness for energy feel
          segmentsMin: 12,
          segmentsRange: 6,
          curveSegments: 20,
        },
        spawn: {
          maxArcs: config.maxArcs,
          spawnInterval: config.spawnInterval,
          lifetime: config.arcLifetime,
          settlementDelay: 0,
          sineSpeed: 0,
        },
        style: {
          // Gold/yellow lightning for the inner energy
          glowLayers: [
            [0.1, 0.15, false],
            [0.06, 0.28, false],
            [0.03, 0.45, false],
            [0.015, 0.65, false],
            [0.006, 1.0, true],
          ],
          glowLayersMobile: [
            [0.05, 0.3, false],
            [0.025, 0.55, false],
            [0.008, 1.0, true],
          ],
          glowColor: { r: 1.0, g: 0.85, b: 0.4 }, // Gold/yellow
          coreColor: { r: 1.0, g: 1.0, b: 0.9 }, // Warm white
        },
        ...arcsOptions.config,
      },
    });

    this.orbConfig = config;

    // Calculate orb dimensions (matching OrbParticles.ts)
    // Orb uses size * 1.3 in morph-particles, and radius = size * 0.7
    this.radius = config.size * 1.3 * 0.7;
  }

  /**
   * Set the transition progress (0-1) for fading in/out with shape transitions
   */
  public setTransitionProgress(progress: number): void {
    this.transitionProgress = Math.max(0, Math.min(1, progress));
    this.setIntensity(this.transitionProgress);
    this.active = progress > 0.1;
  }

  /**
   * Check if arcs are currently active
   */
  public isActive(): boolean {
    return this.active;
  }

  /**
   * Update the orb arcs
   */
  public updateOrb(time: number, deltaTime: number = 1 / 60): void {
    if (!this.active) {
      this.clearArcs();
      return;
    }

    const { spawnInterval, arcLifetime, maxArcs } = this.orbConfig;
    const [minInterval, maxInterval] = spawnInterval;

    // Try to spawn multiple arcs - each slot has independent spawn chance
    const currentArcCount = this.getArcCount();
    const availableSlots = maxArcs - currentArcCount;

    for (let slot = 0; slot < availableSlots; slot++) {
      const baseChance = deltaTime / ((minInterval + maxInterval) / 2);
      const spawnChance = baseChance * (0.5 + Math.random() * 0.5);

      if (Math.random() < spawnChance) {
        this.spawnOrbArc(arcLifetime);
      }
    }

    // Update existing arcs
    this.update(deltaTime);
  }

  /**
   * Spawn a single orb arc from center to a random point on inner surface
   */
  private spawnOrbArc(arcLifetime: [number, number]): void {
    // Start from exact center of the orb
    const startPoint = new Vector3(0, 0, 0);

    // End point: random direction inside gold core (safe margin from blue shell)
    // Gold particles fill 0-100% of radius, blue is at 98%+
    // Use 70% of radius to stay inside the gold area
    const endRadius = this.radius * 0.7;

    // Random direction using spherical coordinates
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(2 * Math.random() - 1);

    const endPoint = new Vector3(
      endRadius * Math.sin(phi) * Math.cos(theta),
      endRadius * Math.sin(phi) * Math.sin(theta),
      endRadius * Math.cos(phi),
    );

    const [minLife, maxLife] = arcLifetime;
    this.spawnArc({
      startPoint,
      endPoint,
      lifetime: minLife + Math.random() * (maxLife - minLife),
      intensity: 0.7 + Math.random() * 0.3,
    });
  }

  public dispose(): void {
    super.dispose();
  }
}
